/**
 * historyDB.js — IndexedDB storage for undo/redo history AND recording frames
 * 
 * Stores canvas data on DISK instead of RAM to prevent
 * browser freezing during long painting sessions.
 * 
 * Two stores:
 *  1. 'frames' — undo/redo history (ImageData as ArrayBuffer)
 *  2. 'recording' — timelapse recording frames (JPEG data URLs)
 */

const DB_NAME = 'comica_history';
const DB_VERSION = 2;
const HISTORY_STORE = 'frames';
const RECORDING_STORE = 'recording';

let dbInstance = null;
let dbPendingPromise = null; // Prevents race condition on parallel opens

/**
 * Open (or create) the IndexedDB database — cached + race-safe
 */
function openDB() {
    if (dbInstance) return Promise.resolve(dbInstance);
    if (dbPendingPromise) return dbPendingPromise; // Reuse in-flight open

    dbPendingPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(HISTORY_STORE)) {
                db.createObjectStore(HISTORY_STORE, { keyPath: 'step' });
            }
            if (!db.objectStoreNames.contains(RECORDING_STORE)) {
                db.createObjectStore(RECORDING_STORE, { keyPath: 'idx' });
            }
        };

        request.onsuccess = (e) => {
            dbInstance = e.target.result;
            dbPendingPromise = null;
            resolve(dbInstance);
        };

        request.onerror = (e) => {
            dbPendingPromise = null;
            console.error('IndexedDB open error:', e.target.error);
            reject(e.target.error);
        };
    });

    return dbPendingPromise;
}

// ══════════════════════════════════════════════════════════════
//  UNDO/REDO HISTORY
// ══════════════════════════════════════════════════════════════

/**
 * Save a history entry to IndexedDB
 * Accepts raw ImageData — no buffer cloning needed (IndexedDB handles it)
 */
export async function saveHistoryEntry(step, layerId, imageData, layersMeta) {
    try {
        const db = await openDB();

        // Pass buffer directly — IndexedDB internally clones during structured clone
        // No need for .slice(0) which was causing a 12MB extra copy per save
        const entry = {
            step,
            layerId: String(layerId),
            buffer: imageData.data.buffer,
            width: imageData.width,
            height: imageData.height,
            layers: layersMeta
        };

        return new Promise((resolve, reject) => {
            const tx = db.transaction(HISTORY_STORE, 'readwrite');
            const store = tx.objectStore(HISTORY_STORE);
            const req = store.put(entry);

            req.onsuccess = () => {
                // Keep only the last 10 entries to prevent DB bloat
                const countReq = store.count();
                countReq.onsuccess = () => {
                    if (countReq.result > 10) {
                        const excess = countReq.result - 10;
                        let deleted = 0;
                        const cursorReq = store.openCursor();
                        cursorReq.onsuccess = (e) => {
                            const cursor = e.target.result;
                            if (cursor && deleted < excess) {
                                cursor.delete();
                                deleted++;
                                cursor.continue();
                            } else {
                                resolve();
                            }
                        };
                    } else {
                        resolve();
                    }
                };
            };
            req.onerror = (e) => reject(e.target.error);
        });
    } catch (err) {
        console.error('saveHistoryEntry failed:', err);
    }
}

/**
 * Load a history entry from IndexedDB
 */
export async function loadHistoryEntry(step) {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(HISTORY_STORE, 'readonly');
            const store = tx.objectStore(HISTORY_STORE);
            const req = store.get(step);
            req.onsuccess = () => {
                const entry = req.result;
                if (!entry) { resolve(null); return; }
                const clampedArray = new Uint8ClampedArray(entry.buffer);
                const imageData = new ImageData(clampedArray, entry.width, entry.height);
                resolve({ layerId: entry.layerId, imageData, layers: entry.layers });
            };
            req.onerror = (e) => reject(e.target.error);
        });
    } catch (err) {
        console.error('loadHistoryEntry failed:', err);
        return null;
    }
}

/**
 * Prune old history entries — only keeps entries with steps in validSteps
 */
export async function pruneHistory(validSteps) {
    try {
        const db = await openDB();
        const validSet = new Set(validSteps);
        return new Promise((resolve, reject) => {
            const tx = db.transaction(HISTORY_STORE, 'readwrite');
            const store = tx.objectStore(HISTORY_STORE);
            const req = store.openCursor();
            req.onsuccess = (e) => {
                const cursor = e.target.result;
                if (cursor) {
                    if (!validSet.has(cursor.key)) cursor.delete();
                    cursor.continue();
                } else {
                    resolve();
                }
            };
            req.onerror = (e) => reject(e.target.error);
        });
    } catch (err) {
        console.error('pruneHistory failed:', err);
    }
}

/**
 * Clear all history
 */
export async function clearHistory() {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(HISTORY_STORE, 'readwrite');
            const store = tx.objectStore(HISTORY_STORE);
            const req = store.clear();
            req.onsuccess = () => resolve();
            req.onerror = (e) => reject(e.target.error);
        });
    } catch (err) {
        console.error('clearHistory failed:', err);
    }
}

// ══════════════════════════════════════════════════════════════
//  RECORDING FRAMES (Timelapse)
// ══════════════════════════════════════════════════════════════

/**
 * Save a recording frame (JPEG Blob) to IndexedDB
 */
export async function saveRecordingFrame(idx, blob) {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(RECORDING_STORE, 'readwrite');
            const store = tx.objectStore(RECORDING_STORE);
            const req = store.put({ idx, blob });
            req.onsuccess = () => resolve();
            req.onerror = (e) => reject(e.target.error);
        });
    } catch (err) {
        console.error('saveRecordingFrame failed:', err);
    }
}

/**
 * Load a single recording frame by index
 * Returns a Blob which can be converted to an object URL
 */
export async function loadRecordingFrame(idx) {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(RECORDING_STORE, 'readonly');
            const store = tx.objectStore(RECORDING_STORE);
            const req = store.get(idx);
            req.onsuccess = () => {
                resolve(req.result ? req.result.blob : null);
            };
            req.onerror = (e) => reject(e.target.error);
        });
    } catch (err) {
        console.error('loadRecordingFrame failed:', err);
        return null;
    }
}

/**
 * Get total count of recording frames
 */
export async function getRecordingFrameCount() {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(RECORDING_STORE, 'readonly');
            const store = tx.objectStore(RECORDING_STORE);
            const req = store.count();
            req.onsuccess = () => resolve(req.result);
            req.onerror = (e) => reject(e.target.error);
        });
    } catch (err) {
        console.error('getRecordingFrameCount failed:', err);
        return 0;
    }
}

/**
 * Clear all recording frames
 */
export async function clearRecordingFrames() {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(RECORDING_STORE, 'readwrite');
            const store = tx.objectStore(RECORDING_STORE);
            const req = store.clear();
            req.onsuccess = () => resolve();
            req.onerror = (e) => reject(e.target.error);
        });
    } catch (err) {
        console.error('clearRecordingFrames failed:', err);
    }
}
